import { clearDevTeacher, getDevTeacherToken } from './devToken';
import {
  ADVANCE_IN_PROGRESS,
  SESSION_CORRUPTED,
  SESSION_VERSION_CONFLICT,
} from './generated/error-codes';
import {
  DEV_PASSWORD_PATHS,
  MULTI_ROLE_AUTH_PATHS,
  STUDENT_AUTH_PATHS,
  TEACHER_AUTH_PATHS,
} from './generated/route-headers';

const BASE = '/api';

// ── [BEGIN temporary site password header — remove when public launch happens] ─
// The backend enforces a shared-secret header on every API call during
// private testing. Read the password from localStorage (set by SiteGate).
const SITE_PASSWORD_KEY = 'siteAccess';
// ── [END temporary site password header] ─────────────────────────────────

const STUDENT_TOKEN_KEY = 'studentToken';

// Clerk's useAuth().getToken() isn't accessible outside a React component.
// ClerkTokenBridge mounts inside ClerkProvider and stashes the latest
// getter here so the fetch wrapper can reach it.
let clerkGetToken: () => Promise<string | null> = async () => null;

export function setClerkTokenGetter(fn: () => Promise<string | null>) {
  clerkGetToken = fn;
}

export function getStoredStudentToken(): string | null {
  return localStorage.getItem(STUDENT_TOKEN_KEY);
}

export function setStoredStudentToken(token: string | null) {
  if (token) localStorage.setItem(STUDENT_TOKEN_KEY, token);
  else localStorage.removeItem(STUDENT_TOKEN_KEY);
}

/**
 * Probe whether the cached student token still verifies against the
 * current backend (issue #109). Returns true on a 200 from
 * ``/api/auth/student-me``; false on a missing token, a 401, or a
 * network failure. Used by ``DevQuickstart`` on mount to detect that
 * the cached "ready" panel is showing for a backend session whose
 * ``STUDENT_JWT_SECRET`` differs from when the token was issued
 * (most often: local dev process restart, staging redeploy, or the
 * env var being newly set after the original token was minted).
 *
 * Bypasses ``request<T>``'s auto-recovery (which would hard-redirect
 * on 401) — this is an on-mount validation where we want the caller
 * to handle the "you're stale" outcome by updating local UI state,
 * not by yanking the page out from under itself.
 */
export async function validateStudentToken(): Promise<boolean> {
  const token = getStoredStudentToken();
  if (!token) return false;
  try {
    const res = await fetchWithTimeout(`${BASE}/auth/student-me`, {
      headers: await composedHeaders('/auth/student-me'),
    });
    return res.ok;
  } catch {
    // Network failure or timeout → don't pessimistically wipe; assume
    // the token is fine and let the user's next real request surface
    // any issue.
    return true;
  }
}

// Path-based routing: teacher paths get the Clerk bearer, student paths
// get the HS256 student token, login/probe get neither.
//
// The path-prefix tables below are CODEGEN — sourced from FastAPI's
// ``app.routes`` registry via ``scripts/gen-route-headers.py``. Adding
// a new authed backend route under a new path prefix is now
// auto-propagated to the frontend's auth-header attachment; CI
// (``.github/workflows/codegen-drift.yml``) fails any PR that adds a
// route without regenerating. See
// ``V3/frontend/src/api/generated/route-headers.ts`` for the current
// tables, and the audit's ``auth-header-coverage`` lens for the
// remaining failure mode this codegen can't self-heal (a new backend
// auth dep being introduced without updating the script's
// ``DEP_HEADERS`` table).
//
// Historical context — three real bugs in a single day (eehae#549,
// eehae#559, eehae#568) all fit the pattern "backend added a path
// prefix; matcher didn't know about it." Codegen eliminates the
// recurring miss.
function matchesAny(path: string, prefixes: ReadonlyArray<string>): boolean {
  // Strip query string + fragment before matching — callers may pass
  // ``/sessions?limit=20`` or ``/teacher/me#anchor``, and the prefix
  // match must ignore those segments. Without this, a path with a
  // query string fails both the exact-match arm (full path !== prefix)
  // AND the startsWith arm (the next char after the prefix is "?" or
  // "#", not "/"), so the matcher silently drops the auth header.
  // CodeRabbit on PR #569.
  const cleanPath = path.split(/[?#]/, 1)[0];
  return prefixes.some((p) => cleanPath === p || cleanPath.startsWith(p + '/'));
}

function isTeacherPath(path: string): boolean {
  return matchesAny(path, TEACHER_AUTH_PATHS);
}

function isStudentPath(path: string): boolean {
  return matchesAny(path, STUDENT_AUTH_PATHS);
}

function isMultiRolePath(path: string): boolean {
  return matchesAny(path, MULTI_ROLE_AUTH_PATHS);
}

async function authHeaders(path: string): Promise<Record<string, string>> {
  // Multi-role routes (backed by ``get_current_principal``, e.g.
  // ``POST /api/me/explain-decision/{slug}`` — eehae#581) accept EITHER
  // a student bearer OR a teacher/admin bearer. The branch fires BEFORE
  // the teacher/student branches so the path doesn't get classified
  // exclusively into one role bucket and silently drop the other
  // role's token.
  //
  // Precedence — student token first, then dev teacher, then Clerk:
  //   - Student first is the lighter check (no Clerk roundtrip) AND
  //     matches the most-common caller of these endpoints (student-
  //     facing explain-decision UI under PIPA Art. 37-2 §2). Sending
  //     a student token to a route a teacher would also be allowed on
  //     is correct — ``get_current_principal`` dispatches on the
  //     token's claim shape (``student_pk`` → student verifier,
  //     ``sub`` → Clerk verifier) so the right verification path runs.
  //   - Dev teacher token next so the existing dev-quickstart harness
  //     keeps working when a teacher operator hits the endpoint.
  //   - Clerk JWT last (the expensive async call).
  if (isMultiRolePath(path)) {
    const studentToken = getStoredStudentToken();
    if (studentToken) return { Authorization: `Bearer ${studentToken}` };
    const devToken = getDevTeacherToken();
    if (devToken) return { Authorization: `Bearer ${devToken}` };
    const clerkToken = await clerkGetToken();
    return clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {};
  }
  if (isTeacherPath(path)) {
    // Dev test-harness bypass (set by /dev/quickstart): when a dev teacher
    // token is stashed locally, use it instead of hitting Clerk for a
    // bearer. The backend's auth/deps.py accepts it only when DEV_TOOLS=1.
    const devToken = getDevTeacherToken();
    if (devToken) return { Authorization: `Bearer ${devToken}` };
    const token = await clerkGetToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  if (isStudentPath(path)) {
    const token = getStoredStudentToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  return {};
}

async function composedHeaders(
  path: string,
  extra?: HeadersInit,
): Promise<Headers> {
  const h = new Headers(extra);
  const auth = await authHeaders(path);
  for (const [k, v] of Object.entries(auth)) h.set(k, v);
  // [site-password] remove block when public launch happens — SiteGate
  // (and the X-Site-Password header it backs) all go away together.
  const pw = localStorage.getItem(SITE_PASSWORD_KEY);
  if (pw) h.set('X-Site-Password', pw);
  // [dev-password] /dev/* mutation endpoints (provider switcher
  // mutations, dev_quickstart endpoints) gate on the X-Dev-Password
  // header via ``routers/dev_common.py::require_dev_password``. The
  // backend accepts ``SITE_PASSWORD`` as the dev-password fallback
  // (see ``_expected_dev_password``), so on staging where the operator
  // has configured one shared secret we send the same ``siteAccess``
  // value here. If ``DEV_QUICKSTART_PASSWORD`` is ever set to a
  // distinct value from ``SITE_PASSWORD``, this needs a separate
  // localStorage key + UI to collect it — out of scope until that
  // configuration actually exists in the wild.
  //
  // DEV_PASSWORD_PATHS is CODEGEN (see ``generated/route-headers.ts``);
  // only routes whose backend dep is ``require_dev_password`` get the
  // header attached. Tighter than the original ``startsWith('/dev/')``
  // match — body-password endpoints (``/dev/quickstart`` etc.) no
  // longer carry the harmless-but-redundant header.
  if (pw && matchesAny(path, DEV_PASSWORD_PATHS)) {
    h.set('X-Dev-Password', pw);
  }
  return h;
}

/**
 * HTTP error from the backend. Carries the response ``status`` so
 * callers can branch on specific failure modes (e.g. distinguish a
 * genuine 404 "this resource doesn't exist" from a transient 5xx
 * or a 401 from missing auth).
 *
 * ``code`` carries the backend's optional ``error_code`` body field
 * (issue #977). It disambiguates same-status errors that need different
 * client handling — most importantly the two 409s on
 * ``/sessions/{slug}/advance``: a #106 stale-version conflict
 * (``session_version_conflict`` → "updated elsewhere", reload) vs. an
 * idempotency-in-flight 409 (``advance_in_progress`` → retry/await the
 * in-flight winner, NO reload dialog). ``null`` when the response
 * carried no ``error_code`` (every endpoint that doesn't emit one).
 *
 * Use ``isApiError(e)`` to narrow in catch blocks; falling back to
 * the generic ``Error`` shape is fine for "just show the message".
 */
export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/**
 * True iff ``e`` is the TRUE #106 optimistic-version conflict 409
 * (issue #977) — the only 409 that should surface the "this session was
 * updated elsewhere — reload" dialog. Matches on the stable
 * ``error_code`` body field (codegen-pinned, ``SESSION_VERSION_CONFLICT``)
 * rather than on status alone, so the idempotency-in-flight 409 isn't
 * mis-classified as a concurrent edit.
 */
export function isStaleVersionConflict(e: unknown): boolean {
  return isApiError(e) && e.status === 409 && e.code === SESSION_VERSION_CONFLICT;
}

/**
 * True iff ``e`` is the idempotency-in-flight 409 (issue #977): a
 * same-key request (or, for ``/advance``, a concurrent transition) is
 * still being processed. The correct UX is to retry/await the in-flight
 * winner — NOT to pop the stale-version reload dialog. Matches on the
 * stable ``advance_in_progress`` ``error_code``.
 */
export function isAdvanceInProgress(e: unknown): boolean {
  return isApiError(e) && e.status === 409 && e.code === ADVANCE_IN_PROGRESS;
}

/**
 * True iff ``e`` is the corrupted-session 409 (#108) the streaming endpoint
 * ``POST /{slug}/message-stream`` returns pre-stream when the session is
 * ``corrupted``. Like the stale-version conflict it wants a refresh →
 * recovery-UI reload, but it's a DIFFERENT condition (the session is
 * durably broken, not concurrently edited), so it carries its own stable
 * ``session_corrupted`` ``error_code``. ``handleStaleSessionConflict``
 * matches this alongside ``isStaleVersionConflict`` so #977's narrowing
 * off bare ``status === 409`` matching doesn't silently drop the
 * corruption-recovery path into a raw alert().
 */
export function isSessionCorrupted(e: unknown): boolean {
  return isApiError(e) && e.status === 409 && e.code === SESSION_CORRUPTED;
}

/**
 * Thrown by ``apiPostSSE`` when a chunk-timeout fires mid-stream: no
 * chunk — neither a token delta NOR a backend ``:heartbeat`` comment —
 * arrived within ``STREAM_CHUNK_TIMEOUT_MS`` (issue #163 item 3).
 *
 * This is the recoverable "connection lost" signal. The backend emits a
 * heartbeat every ~5s during a streamed response (see
 * ``routers/sessions.py`` + ``services/session_phases.py``), so a
 * healthy-but-slow stream keeps resetting the timer. The timer only
 * runs out when the wire actually goes dead — a dropped TCP connection,
 * a stalled reverse proxy, a backend that died mid-stream — i.e. exactly
 * the cases that previously hung on an infinite "Waiting for tutor…"
 * spinner with no recovery path.
 *
 * Distinct from ``ApiError`` so the consuming hook can branch: an
 * ``ApiError(409)`` is a stale-session conflict (reload prompt), an
 * ``ApiError(5xx)`` is a pre-stream HTTP failure, but a
 * ``StreamConnectionLostError`` means "the stream went silent — offer a
 * Retry" rather than a hard error.
 */
export class StreamConnectionLostError extends Error {
  constructor(message = 'Connection lost') {
    super(message);
    this.name = 'StreamConnectionLostError';
  }
}

export function isStreamConnectionLostError(
  e: unknown,
): e is StreamConnectionLostError {
  return e instanceof StreamConnectionLostError;
}

/**
 * Per-chunk read timeout for ``apiPostSSE`` (issue #163 item 3). If no
 * chunk arrives within this window the read is aborted and a
 * ``StreamConnectionLostError`` is thrown. Sized at 3x the backend's 5s
 * heartbeat interval so two consecutive heartbeats can be lost (a brief
 * network hiccup) before we declare the connection dead — a deliberate
 * margin against false positives on a flaky-but-recovering link.
 */
export const STREAM_CHUNK_TIMEOUT_MS = 15000;

/**
 * Extract a user-displayable message from an unknown thrown value.
 *
 * Catch-block pairing for the strict-typed ``catch (e: unknown)`` shape
 * (issue #79 — replacing pervasive ``catch (e: any)``). Returns the
 * Error's ``message`` field if it's an Error instance; falls back to
 * ``String(e)`` so primitive throws (rare but legal in JS) still
 * surface something useful.
 *
 * Use:
 *
 *   try { await something() } catch (e: unknown) {
 *     alert('Something went wrong: ' + errorMessage(e))
 *   }
 *
 * If the caller wants HTTP-level branching, ``isApiError(e)`` narrows
 * to ApiError first and gives access to the ``status`` field too.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/**
 * Fully clear the dev test session from localStorage.
 *
 * Mirrors ``handleExit`` in DevQuickstart.tsx. Used by the 401-recovery
 * path below — when a teacher-path call comes back unauthorized while
 * a dev token is present, the token is stale (backend's dev-token
 * registry was wiped, the 12h TTL elapsed, the persisted
 * ``data/dev_tokens.json`` got out of sync, etc.) and the right
 * recovery is to wipe the local session and bounce to /dev/quickstart.
 */
function wipeDevSession(): void {
  clearDevTeacher();
  try {
    localStorage.removeItem(STUDENT_TOKEN_KEY);
    localStorage.removeItem('studentLoginData');
    localStorage.removeItem('teacherClassCode');
  } catch {
    // localStorage blocked / unavailable — best effort.
  }
}

/**
 * Detect a stale dev token and redirect to /dev/quickstart for re-auth.
 *
 * Fires only when:
 *   - The backend returned 401, AND
 *   - The path requires teacher auth, AND
 *   - localStorage has a dev teacher token (i.e. we're in dev mode).
 *
 * In production this never triggers (no dev token in localStorage).
 * Returns true when recovery was triggered; the caller should still
 * throw a normal ``ApiError`` so promise chains unwind, but the
 * navigation will land on /dev/quickstart before that matters.
 */
function recoverFromStaleDevToken(status: number, path: string): boolean {
  if (status !== 401) return false;
  if (!isTeacherPath(path)) return false;
  if (!getDevTeacherToken()) return false;
  wipeDevSession();
  if (typeof window !== 'undefined') {
    // Hard navigation, blowing away any in-memory state. DevQuickstart
    // will see no token in localStorage and render the password form.
    window.location.href = '/dev/quickstart';
  }
  return true;
}

/**
 * Wipe the student-side localStorage entries. Mirrors the teacher-side
 * ``wipeDevSession`` minus the dev teacher cookie — student auth state
 * is its own cluster (token + restored login data + cohort/login meta
 * the SplashScreen reads).
 */
function wipeStudentSession(): void {
  try {
    localStorage.removeItem(STUDENT_TOKEN_KEY);
    localStorage.removeItem('studentLoginData');
  } catch {
    // localStorage blocked / unavailable — best effort.
  }
}

/**
 * Detect a stale student token and bounce to the appropriate re-auth
 * page (issue #109 — symmetric twin of ``recoverFromStaleDevToken``).
 *
 * Fires only when ALL of:
 *   - The backend returned 401, AND
 *   - The path requires student auth, AND
 *   - localStorage has a student token (i.e. we have one to invalidate), AND
 *   - The 401 detail looks like an auth-layer rejection (signature
 *     verification failed, token revoked, expired, student not found)
 *     rather than a generic "missing bearer token" 401.
 *
 * The detail-string check matters because the backend's
 * ``_try_active_student`` silently falls back to ``test-student-01``
 * when the active token is invalid — so the dev-quickstart flow can
 * succeed at POST while the subsequent ``GET /api/sessions/{id}``
 * 401s with the actual signature-verification error. We want to fire
 * recovery on that signature-verification flavour but NOT on a
 * generic "you're just not logged in" 401 (no token to wipe in that
 * case anyway, but defense-in-depth).
 *
 * Where to redirect:
 *   - If a dev teacher token is also stashed → ``/dev/quickstart``
 *     (user came in via the dev harness; let them re-enter the
 *     quickstart password to re-issue both tokens together).
 *   - Otherwise → ``/`` (real student-login flow lives on the splash).
 *
 * Returns true when recovery was triggered; the caller still throws a
 * normal ``ApiError`` so promise chains unwind, but the navigation
 * will land elsewhere before that matters.
 */
/**
 * Substring matchers (lowercase) for the 401 detail strings that mean
 * "the student's stored token is no longer valid — wipe and re-auth."
 *
 * Each substring is meant to catch a specific backend error variant
 * (see ``docs/api-errors.yaml``):
 *   - 'student token'      → AUTH_INVALID_STUDENT_TOKEN_PREFIX
 *   - 'revoked'            → AUTH_STUDENT_TOKEN_REVOKED
 *   - 'expired'            → AUTH_STUDENT_TOKEN_EXPIRED
 *   - 'student not found'  → AUTH_STUDENT_NOT_FOUND
 *
 * Substring matching (rather than exact equality on the registry
 * constants) is intentional: the backend f-string variant carries a
 * dynamic JWT-error suffix, and the substring shape absorbs that
 * variance without per-variant case branches.
 *
 * Exported so ``client.test.ts`` can assert each registry constant
 * still matches one of these patterns — when the backend updates a
 * registry value via codegen, that defensive test catches a stale
 * pattern array immediately rather than at the next failed recovery.
 */
export const STALE_STUDENT_TOKEN_PATTERNS = [
  'student token',
  'revoked',
  'expired',
  'student not found',
] as const;

function recoverFromStaleStudentToken(
  status: number,
  path: string,
  detail: string,
): boolean {
  if (status !== 401) return false;
  if (!isStudentPath(path)) return false;
  if (!getStoredStudentToken()) return false;
  const detailLower = (detail || '').toLowerCase();
  if (!STALE_STUDENT_TOKEN_PATTERNS.some((p) => detailLower.includes(p))) {
    return false;
  }
  wipeStudentSession();
  if (typeof window !== 'undefined') {
    const target = getDevTeacherToken() ? '/dev/quickstart' : '/';
    window.location.href = target;
  }
  return true;
}

/**
 * 401 recovery for multi-role routes (``get_current_principal``,
 * eehae#581 / PR #601 CodeRabbit). The role-specific helpers
 * (``recoverFromStaleDevToken``, ``recoverFromStaleStudentToken``)
 * gate on ``isTeacherPath`` / ``isStudentPath`` and so silently
 * skip multi-role paths like ``/me/explain-decision/*`` — without
 * this helper, a stale student token or stale dev teacher token
 * on a multi-role call kept being resent on every retry and left
 * the user stranded behind the revocation banner instead of
 * landing on the right re-auth surface.
 *
 * Disambiguation is deterministic: ``authHeaders()`` picks the
 * token in a fixed precedence (student → dev → Clerk) and reads
 * localStorage synchronously, so we can re-evaluate that same
 * precedence here and know exactly which token was sent. (No code
 * path mutates the relevant localStorage keys between
 * ``composedHeaders()`` and the response handler, so this
 * re-evaluation is safe.)
 *
 * Behaviour by picked kind:
 *   - ``student``: mirror ``recoverFromStaleStudentToken``'s gated
 *     wipe + redirect (only fire on a stale-token detail pattern;
 *     a generic ``unauthorized`` is left to the revocation banner
 *     since there's nothing legitimately recoverable).
 *   - ``dev``: mirror ``recoverFromStaleDevToken`` — any 401 with a
 *     dev token present means the registry no longer knows the
 *     token (TTL elapsed, restart wiped the on-disk registry,
 *     etc.); wipe + bounce to /dev/quickstart.
 *   - ``clerk``: no local recovery (Clerk owns its own refresh
 *     lifecycle); fall through to the revocation banner.
 */
function recoverFromStaleMultiRoleToken(
  status: number,
  path: string,
  detail: string,
): boolean {
  if (status !== 401) return false;
  if (!isMultiRolePath(path)) return false;
  // Re-evaluate the same precedence used by ``authHeaders``. Whatever
  // ``authHeaders`` picked at request time is what the backend just
  // rejected — assuming the local token state hasn't mutated
  // in-flight (it doesn't on this code path).
  if (getStoredStudentToken()) {
    const detailLower = (detail || '').toLowerCase();
    if (!STALE_STUDENT_TOKEN_PATTERNS.some((p) => detailLower.includes(p))) {
      return false;
    }
    wipeStudentSession();
    if (typeof window !== 'undefined') {
      const target = getDevTeacherToken() ? '/dev/quickstart' : '/';
      window.location.href = target;
    }
    return true;
  }
  if (getDevTeacherToken()) {
    wipeDevSession();
    if (typeof window !== 'undefined') {
      window.location.href = '/dev/quickstart';
    }
    return true;
  }
  // Clerk-picked (or no token at all) — Clerk handles its own
  // session refresh; let the revocation-banner dispatch fire.
  return false;
}

/**
 * Custom-event name + payload shape for the revocation-banner signal
 * (#306 surface 5). Fired when the backend returns 401/403 AND the
 * existing auto-recovery functions (which hard-redirect to splash /
 * dev-quickstart) didn't claim it. That gap is the
 * "Clerk-authed teacher whose org membership was revoked" /
 * "any caller with a 403 from server-side permission denial" case —
 * their existing token / Clerk session keeps working until TTL but
 * the backend rejects every protected call. The banner is the UX
 * signal that bridges that gap until the user reloads.
 *
 * Listener: ``components/RevocationBanner.tsx``, mounted at app root.
 */
export const REVOCATION_EVENT_NAME = 'api:auth-revoked'

export interface RevocationEventDetail {
  status: number
  path: string
}

/**
 * Revocation-banner signal (#306 surface 5). Called from every
 * helper's non-OK branch. Fires the ``api:auth-revoked`` event
 * ONLY when:
 *   - The status is 401 or 403, AND
 *   - Neither auto-recovery function fired (they'll hard-redirect
 *     on success — no banner needed in that path).
 *
 * The banner component does additional gating (only shows if the
 * user has a credential of some kind) so a logout-flow 401 on
 * ``/auth/student-me`` from a no-credential page doesn't trip the
 * banner.
 *
 * Extracted into a helper because every error-path helper
 * (``request``, ``apiPostForm``, ``apiDelete``, ``apiGetBlob``,
 * ``apiPostSSE``) needs to call it after the two recovery
 * functions — without the extraction the dispatch was only wired
 * into ``request()``, leaving the other helpers' 401/403 paths
 * unable to surface the banner. CodeRabbit on PR #369.
 */
function maybeDispatchRevocationEvent(
  status: number,
  path: string,
  devHandled: boolean,
  studentHandled: boolean,
  multiRoleHandled: boolean = false,
): void {
  if (
    (status === 401 || status === 403) &&
    !devHandled &&
    !studentHandled &&
    !multiRoleHandled &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(
      new CustomEvent<RevocationEventDetail>(REVOCATION_EVENT_NAME, {
        detail: { status, path },
      }),
    )
  }
}

// ── Fetch timeout ────────────────────────────────────────────────────────────
// All non-streaming API calls must resolve (or fail) within this window.
// Short enough that a stalled request surfaces a clean error instead of
// hanging indefinitely; long enough not to clip a legitimately-slow call.
//
// Sized at 60 s (was 30 s): the only LLM-bearing endpoint behind this timeout
// is the blocking assessment answer-eval (POST /sessions/{slug}/message →
// evaluate_answer); the build phases run in the background queue — except the
// selective phase-retry POST, which runs one phase synchronously and overrides
// per-call with ``PHASE_RETRY_TIMEOUT_MS`` below — and the tutoring chat
// streams over the exempt SSE path. Staging telemetry showed
// that path is normally ~5 s, but the provider-latency tail spikes well past
// 30 s on small payloads (observed two non-erroring evals at 40.6 s / 27.0 s
// on OpenRouter qwen3 within one 10-min window). A 30 s cap would convert
// those successful evals into a 504 shown to the student — exactly the path
// we least want to fail. 60 s covers the observed tail with margin while
// still bounding a truly hung backend. Revisit once real pilot traffic shows
// whether the >30 s tail is rare or common.
//
// The SSE/streaming path (``apiPostSSE``) intentionally does NOT use this
// timeout — chunk-level timeouts are owned by issue #163 item 3.
const FETCH_TIMEOUT_MS = 60_000;

// Override timeout for ``apiPostForm`` — used by the two PDF-upload routes
// (``ModuleRow`` module upload and ``MaterialUpload`` class-material upload).
// 60 s is far too tight for these: the upload route runs the full PDF parse
// cascade (opendataloader JAR → pypdf → Tesseract OCR) synchronously inside
// the request, and a 10-20 page scanned PDF lands at 60-150 s of OCR alone
// (#919 staging incident: 10 pages → 150 s).
//
// Sized to comfortably outlast the **backend's outer ``asyncio.wait_for``
// cliff**, not just the inner parse deadline, plus headroom for network
// latency + 503 response serialization:
//
//   backend inner deadline   = ``PDF_PARSE_TIMEOUT_SECONDS``      (600 s)
//   backend outer wait_for   = ``_OUTER_PARSE_TIMEOUT_SECONDS``   (605 s)
//   frontend abort           = ``UPLOAD_FETCH_TIMEOUT_MS``        (630 s)
//                                                                 = +25 s
//
// The +25 s headroom covers: (a) upload network latency — multi-MB
// multipart bodies can take seconds before the backend even starts the
// 605 s clock, (b) response-serialization + Fly proxy egress on the 503
// path. CodeRabbit on PR #929 caught an earlier draft that had 600 s
// here — exactly equal to the backend INNER deadline, leaving a 5 s
// window where the frontend would abort before the backend's outer
// cliff fired and the teacher would see the misleading
// ``ApiError(504, 'Request timeout')`` instead of the intended
// ``503 OCR_FAILED_ERROR``. This value must always exceed the backend's
// ``_OUTER_PARSE_TIMEOUT_SECONDS`` plus realistic wire-time slack; if
// the backend's caps move, this one moves with them.
//
// This is a band-aid on the synchronous-upload-route architecture; the
// proper fix is pushing PDF parse off the upload route into the build queue
// (BuildPhase scaffolding from #408/#409) so the upload can return ``202
// Accepted`` immediately. Tracked separately — see the architectural
// follow-up issue filed alongside this PR (#930).
const UPLOAD_FETCH_TIMEOUT_MS = 630_000;

// Override timeout for the selective build-phase retry POST
// (``/admin/modules/{id}/build/phase/{phase}/retry`` — consumed by
// ``pages/AdminBuildPhases.tsx`` via ``apiPost``'s ``timeoutMs`` option).
//
// The backend runs the phase SYNCHRONOUSLY inside the request, and its
// extractor calls ride the 🟧 async-build LLM budget
// (``LLM_BUILD_READ_TIMEOUT_S`` = 300 s per inner call, ~600 s SDK caps
// on some providers; a phase chains extract + translate + persist —
// see ``services/module_build/retry.py``). Under the global 60 s cliff
// the browser would abort a retry the server goes on to finish: the
// operator, shown a spurious 504, re-clicks and starts a concurrent
// duplicate run that races the artifact writes and doubles LLM spend.
// This is a 🟧 reliability surface per the budget philosophy
// (docs/changes/0043-budget-philosophy.md): the operator explicitly
// asked to re-run a build step and the page gates its buttons while
// the call is in flight — size the budget to let the work succeed.
//
// Sized like ``UPLOAD_FETCH_TIMEOUT_MS`` (the PR #929 lesson: always
// exceed the backend's realistic ceiling plus wire-time slack): two
// chained 300 s inner LLM calls + persistence + slack = 630 s. The
// proper long-term fix is making the retry async (202 + ladder
// polling), the same #930-shaped follow-up as the upload route.
export const PHASE_RETRY_TIMEOUT_MS = 630_000;

/**
 * Collapse a concrete request URL into a low-cardinality, PII-free route
 * label suitable as a Sentry tag (#1107).
 *
 * The beacon fires from the shared ``fetchWithTimeout`` / ``request`` path,
 * so EVERY endpoint's URL can land here — not just the session routes. Three
 * identifier shapes get templated out:
 *
 *   - ``sess_...`` session slugs (``/api/sessions/sess_NCJveMOwybg/message``)
 *   - class join codes (``/api/classes/7QX3FZ/dashboard``) — same sensitivity
 *     posture as the ``teacherClassCode`` localStorage redaction in
 *     ``src/sentry.ts``
 *   - numeric ids (student PK in ``/classes/{code}/students/42``,
 *     ``/admin/students/42/reset-password``, module ids, ...)
 *
 * Shipping any of these raw as a Sentry tag would (a) explode tag
 * cardinality — every session/class/student a distinct value, so "how many
 * /message timeouts this week" stops aggregating — and (b) leak an
 * identifier to a foreign error tracker, which the PII-scrubbing posture in
 * ``src/sentry.ts`` exists to avoid. ``URL`` parsing is wrapped so a
 * relative/odd input can never throw on this best-effort path.
 */
function timeoutRouteLabel(url: string): string {
  let path = url;
  try {
    // ``url`` is normally ``${BASE}${path}`` (relative). Resolve against a
    // dummy origin so the pathname extraction works for both relative and
    // absolute inputs; we only keep the path, never the host.
    path = new URL(url, 'http://x').pathname;
  } catch {
    // Leave ``path`` as the raw string — still scrubbed below.
  }
  return (
    path
      // ``sess_...`` slug segment → ``{slug}``. Mirrors the backend slug shape.
      .replace(/\/sess_[A-Za-z0-9_-]+/g, '/{slug}')
      // Class join code — the segment right after ``/classes/``.
      .replace(/\/classes\/[^/]+/g, '/classes/{code}')
      // Any all-numeric segment (student PKs, module ids, ...).
      .replace(/\/\d+(?=\/|$)/g, '/{id}')
  );
}

/**
 * Fire-and-forget client-side timeout beacon (#1107).
 *
 * When a request aborts on the frontend's own timeout — the non-streaming
 * ``FETCH_TIMEOUT_MS`` (60 s) cliff or the SSE ``STREAM_CHUNK_TIMEOUT_MS``
 * (15 s) chunk cliff — nothing was visible server-side: a fast backend
 * whose ``200`` never reached the client looked identical to a genuinely
 * slow backend (the ``sess_NCJveMOwybg`` incident). This emits a countable,
 * alertable Sentry event with just enough signal to tell the two apart —
 * route, elapsed ms, and whether response headers had begun arriving — and
 * NOTHING else (no answer text, no tokens, no slug): PIPA-clean per the
 * ``src/sentry.ts`` policy.
 *
 * **Why Sentry, not a backend beacon:** Sentry is already wired
 * (``@sentry/react``, ``src/sentry.ts``) with retention that outlives Fly's
 * minutes-long log window — satisfying both "countable/alertable" and
 * "queryable after logs roll off" with no new endpoint, no new auth
 * surface, and no DB migration. Capture goes through the deferred
 * ``window.Sentry`` handle (attached by ``initSentry``) so this hot-path
 * module never statically imports the SDK.
 *
 * **Never blocks, never throws:** this runs on the user-facing error path
 * (right before the ``ApiError(504)`` / ``StreamConnectionLostError`` is
 * thrown). It must add no latency and must not mask the original error, so
 * the whole body is wrapped — a missing/!init'd Sentry, or any capture
 * failure, is swallowed.
 */
function reportClientTimeout(params: {
  url: string;
  elapsedMs: number;
  kind: 'fetch' | 'stream';
  /** The timeout budget that actually fired, in ms — the per-call value
   *  (e.g. ``UPLOAD_FETCH_TIMEOUT_MS`` on the PDF-upload routes, a caller's
   *  ``chunkTimeoutMs`` override on SSE), not the global default. */
  budgetMs: number;
  /** True if response headers had started arriving before the abort. */
  headersReceived: boolean;
}): void {
  try {
    const sentry = (
      window as unknown as {
        Sentry?: {
          captureMessage?: (
            msg: string,
            ctx?: {
              level?: string;
              tags?: Record<string, string | number | boolean>;
              extra?: Record<string, unknown>;
            },
          ) => void;
        };
      }
    ).Sentry;
    if (!sentry?.captureMessage) return; // Sentry off (local/dev) → no-op.
    const route = timeoutRouteLabel(params.url);
    sentry.captureMessage('client_request_timeout', {
      // ``warning`` not ``error``: a client/network timeout is a quality
      // signal to count + alert on a rate, not a code defect to page on.
      level: 'warning',
      tags: {
        client_timeout: true,
        timeout_kind: params.kind,
        route,
        // Whether the response had STARTED (headers/first chunk seen)
        // before we gave up is the key discriminator: ``false`` points at
        // the request/connection leg never reaching a responsive backend;
        // ``true`` points at a slow body / mid-stream stall.
        headers_received: params.headersReceived,
      },
      extra: {
        elapsed_ms: Math.round(params.elapsedMs),
        // Cliff that fired, for context when triaging.
        timeout_budget_ms: params.budgetMs,
      },
    });
  } catch {
    // Best-effort only — never let the beacon disturb the error path.
  }
}

/**
 * Maps the ``TimeoutError`` raised by the ``AbortSignal.timeout()`` supplied
 * in ``fetchWithTimeout`` to ``ApiError(504, 'Request timeout')``; re-throws
 * anything else unchanged.
 *
 * **Why this is shared, not inlined in ``fetchWithTimeout``:** the timeout
 * signal governs the ENTIRE response lifecycle, not just the initial
 * ``fetch()`` call. Per the Fetch standard, if the signal aborts while
 * ``res.json()`` / ``res.text()`` / ``res.blob()`` is still draining the
 * body, that body-reader promise rejects with the same ``TimeoutError``.
 * ``fetchWithTimeout`` only wraps the headers phase, so the success-path
 * body reads in ``request`` / ``apiPostForm`` / ``apiDelete`` /
 * ``apiGetBlob`` funnel their rejections through here too — otherwise a
 * mid-body timeout (headers arrive fast, body streams slowly past the cap)
 * would surface as a raw ``DOMException`` instead of a clean ``ApiError(504)``.
 *
 * Only ``TimeoutError`` is mapped. ``AbortSignal.timeout()`` always throws
 * ``TimeoutError`` on the browsers this project targets (Vite target es2022:
 * Chrome 94+ / Safari 16+ / Firefox 95+). The older ``AbortError`` name was
 * used by early runtimes before the ``TimeoutError`` spec settled; those are
 * outside our support matrix, so a caller-driven abort (if cancellation is
 * ever wired up) must propagate as-is rather than masquerading as a 504.
 */
function rethrowTimeoutAs504(e: unknown): never {
  if (e instanceof DOMException && e.name === 'TimeoutError') {
    throw new ApiError(504, 'Request timeout');
  }
  throw e;
}

/**
 * ``fetch`` wrapper that aborts with ``ApiError(504, 'Request timeout')``
 * when the response has not started within ``FETCH_TIMEOUT_MS``.
 *
 * Uses ``AbortSignal.timeout()`` (available in all modern browsers and in
 * Node 18+). The DOM lib for ES2023 exposes the static method, so no
 * polyfill is needed at this TS target. On timeout the browser raises a
 * ``DOMException`` whose ``name`` is ``"TimeoutError"``, which
 * ``rethrowTimeoutAs504`` maps to ``ApiError(504)``.
 *
 * **Body-read timeouts:** the same signal also governs body consumption
 * after this function returns — see ``rethrowTimeoutAs504`` for why the
 * callers' ``res.json()`` / ``res.text()`` / ``res.blob()`` reads route
 * their rejections through the same mapping.
 *
 * **Signal override:** this function always supplies its own
 * ``AbortSignal.timeout(timeoutMs)`` and callers MUST NOT pass a
 * ``signal`` in ``options`` — it would be silently overwritten and the
 * caller's abort would become a no-op. If per-request cancellation is
 * ever needed, the right approach is ``AbortSignal.any([customSignal,
 * AbortSignal.timeout(timeoutMs)])`` passed here instead of the bare
 * timeout signal.
 *
 * **Per-route override:** ``timeoutMs`` defaults to ``FETCH_TIMEOUT_MS``
 * (60 s) — the right value for the JSON-API routes (login, classes,
 * sessions, etc.). The two PDF-upload routes via ``apiPostForm`` pass
 * ``UPLOAD_FETCH_TIMEOUT_MS`` (10 min) instead, because synchronous
 * server-side PDF parse can legitimately take minutes on scanned files.
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<Response> {
  const startedAt = Date.now();
  try {
    return await fetch(url, {
      ...options,
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (e) {
    // A ``TimeoutError`` here means the abort fired before ANY response
    // headers arrived (the ``fetch()`` promise itself rejected) —
    // ``headersReceived: false`` (#1107). A mid-body timeout instead
    // surfaces in the callers' ``res.json().catch(rethrowTimeoutAs504)``,
    // which beacons with ``headersReceived: true``. Beacon BEFORE the
    // rethrow so the signal fires even though the user-facing error is the
    // one that propagates.
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      reportClientTimeout({
        url,
        elapsedMs: Date.now() - startedAt,
        kind: 'fetch',
        budgetMs: timeoutMs,
        headersReceived: false,
      });
    }
    rethrowTimeoutAs504(e);
  }
}

/**
 * Core JSON fetch helper used by all public API functions.
 *
 * Calls ``fetchWithTimeout`` (applying ``timeoutMs``, default the 60 s
 * ``FETCH_TIMEOUT_MS``), attaches auth + site-password headers via
 * ``composedHeaders``, and on a non-OK response attempts 401
 * auto-recovery (stale dev token, stale student token, stale
 * multi-role token) before throwing ``ApiError``. Also dispatches the
 * ``api:auth-revoked`` event when a 401/403 is not claimed by recovery.
 */
async function request<T>(
  path: string,
  options?: RequestInit,
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<T> {
  const startedAt = Date.now();
  const res = await fetchWithTimeout(
    `${BASE}${path}`,
    {
      ...options,
      headers: await composedHeaders(path, options?.headers),
    },
    timeoutMs,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText;
    const devHandled = recoverFromStaleDevToken(res.status, path);
    const studentHandled = recoverFromStaleStudentToken(res.status, path, detail);
    const multiRoleHandled = recoverFromStaleMultiRoleToken(res.status, path, detail);
    maybeDispatchRevocationEvent(res.status, path, devHandled, studentHandled, multiRoleHandled);
    // ``error_code`` (#977): optional stable disambiguator the backend
    // attaches to some error bodies (e.g. the two distinct 409s on
    // /advance). Most responses omit it → ``null``. Surfaced on
    // ``ApiError.code`` for matchers like ``isStaleVersionConflict``.
    throw new ApiError(res.status, detail, err.error_code ?? null);
  }
  // ``.catch``: the fetch-level timeout signal also governs body
  // consumption, so a mid-body abort surfaces here (not in
  // ``fetchWithTimeout``) — beacon it (#1107) then map to ``ApiError(504)``
  // like the rest. Headers DID arrive (we reached ``res.ok`` and the body
  // read began), so ``headersReceived: true`` — the discriminator that
  // distinguishes a slow/stalled body from a connection that never reached
  // a responsive backend.
  return res.json().catch((e: unknown) => {
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      reportClientTimeout({
        url: `${BASE}${path}`,
        elapsedMs: Date.now() - startedAt,
        kind: 'fetch',
        // The per-call budget that actually governed this request —
        // the caller's override (e.g. ``PHASE_RETRY_TIMEOUT_MS``) or
        // the 60 s default.
        budgetMs: timeoutMs,
        headersReceived: true,
      });
    }
    return rethrowTimeoutAs504(e);
  });
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPost<T>(
  path: string,
  body?: unknown,
  options?: { idempotencyKey?: string; timeoutMs?: number },
): Promise<T> {
  // Idempotency-Key (issue #159 / Stripe convention): caller-supplied
  // UUID lets the backend dedupe replay of the same logical operation.
  // Today the only callers that pass this are the student-side submit
  // handlers; tomorrow the retry-on-429 layer (#52) will use it too.
  // Backend ignores the header on routes without the idempotency dep,
  // so passing it is safe even on routes that don't dedupe.
  //
  // ``timeoutMs``: per-route override of the global 60 s cliff, the
  // ``apiPost`` sibling of ``apiPostForm``'s hardwired
  // ``UPLOAD_FETCH_TIMEOUT_MS``. First consumer is the build-phase
  // retry POST (``PHASE_RETRY_TIMEOUT_MS`` above) — the backend runs
  // the phase synchronously, so the default cliff would abort a call
  // the server goes on to finish.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  return request<T>(
    path,
    {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    },
    options?.timeoutMs ?? FETCH_TIMEOUT_MS,
  );
}

export function apiPatch<T>(
  path: string,
  body: unknown,
  options?: { idempotencyKey?: string },
): Promise<T> {
  // PATCH variant of ``apiPost`` — same Idempotency-Key + JSON shape,
  // different HTTP verb. Issue #742 introduced the first PATCH consumer
  // (class rename via PATCH /api/classes/{code}); kept here as a
  // sibling of ``apiPost`` so future partial-update endpoints land
  // with the same composition surface (auth headers, 401 retry,
  // error envelope) for free. Body is required (an empty PATCH is
  // 422 by convention on the backend — see classes.update_class).
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  return request<T>(path, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
}

export async function apiPostForm<T>(
  path: string,
  formData: FormData,
  options?: { idempotencyKey?: string },
): Promise<T> {
  // Idempotency-Key (issue #105): same Stripe-style dedupe as ``apiPost``,
  // applied to the multipart upload path. The motivating case is the
  // teacher double-clicking "Add module" while the upload is in flight —
  // without dedupe, two Module rows are created and ``build_module_artifacts``
  // runs twice on the backend, wasting LLM tokens on the duplicate build.
  // Backend ignores the header on routes without the idempotency dep.
  const headers = await composedHeaders(path);
  if (options?.idempotencyKey) {
    headers.set('Idempotency-Key', options.idempotencyKey);
  }
  // 10-min timeout (vs the global 60 s) so the synchronous server-side
  // PDF parse cascade has time to finish on scanned PDFs before the
  // frontend gives up — see ``UPLOAD_FETCH_TIMEOUT_MS`` comment above.
  const res = await fetchWithTimeout(
    `${BASE}${path}`,
    {
      method: 'POST',
      body: formData,
      headers,
    },
    UPLOAD_FETCH_TIMEOUT_MS,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText;
    const devHandled = recoverFromStaleDevToken(res.status, path);
    const studentHandled = recoverFromStaleStudentToken(res.status, path, detail);
    const multiRoleHandled = recoverFromStaleMultiRoleToken(res.status, path, detail);
    maybeDispatchRevocationEvent(res.status, path, devHandled, studentHandled, multiRoleHandled);
    // ``error_code`` (#977): optional stable disambiguator the backend
    // attaches to some error bodies (e.g. the two distinct 409s on
    // /advance). Most responses omit it → ``null``. Surfaced on
    // ``ApiError.code`` for matchers like ``isStaleVersionConflict``.
    throw new ApiError(res.status, detail, err.error_code ?? null);
  }
  // Mid-body timeout maps to ``ApiError(504)`` — see ``rethrowTimeoutAs504``.
  return res.json().catch(rethrowTimeoutAs504);
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: 'DELETE',
    headers: await composedHeaders(path),
  });
  if (!res.ok) {
    // The data-subject-rights DELETE endpoints (issue #129) return a
    // JSON body on success and a structured ``{detail: "..."}`` JSON
    // on failure (e.g. the last-admin guard's 409). Try to parse and
    // fall back to statusText if the body is empty / not JSON — the
    // pre-#129 callers (none of which return a body on success) keep
    // working unchanged.
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText;
    const devHandled = recoverFromStaleDevToken(res.status, path);
    const studentHandled = recoverFromStaleStudentToken(res.status, path, detail);
    const multiRoleHandled = recoverFromStaleMultiRoleToken(res.status, path, detail);
    maybeDispatchRevocationEvent(res.status, path, devHandled, studentHandled, multiRoleHandled);
    // ``error_code`` (#977): optional stable disambiguator the backend
    // attaches to some error bodies (e.g. the two distinct 409s on
    // /advance). Most responses omit it → ``null``. Surfaced on
    // ``ApiError.code`` for matchers like ``isStaleVersionConflict``.
    throw new ApiError(res.status, detail, err.error_code ?? null);
  }
  // PIPA P3 routes (``DELETE /api/{student,teacher,admin}/me``) return
  // a small JSON body with ``deleted_at`` + the configured cascade
  // window so the UI can show the user when their data will be hard-
  // deleted. Older DELETE callers don't depend on a parsed body —
  // we return the parsed JSON if present and ``undefined`` otherwise,
  // preserving the void-returning shape for those.
  // Mid-body timeout maps to ``ApiError(504)`` — see ``rethrowTimeoutAs504``.
  const text = await res.text().catch(rethrowTimeoutAs504);
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}

/**
 * Download a binary response as a Blob. Used by the PIPA P3 (#129)
 * data-subject-rights export endpoints (``GET /api/{student,teacher,
 * admin}/me/export``) which return ``application/zip``. The caller
 * is responsible for triggering the browser download — see
 * ``downloadBlob`` below for the standard create-URL + click-anchor
 * recipe.
 *
 * Filename hint is returned alongside the blob: the backend's
 * ``Content-Disposition: attachment; filename="..."`` header is
 * parsed out so the caller doesn't have to hardcode a name.
 * Falls back to ``null`` if the header is missing.
 */
export async function apiGetBlob(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    headers: await composedHeaders(path),
  });
  if (!res.ok) {
    // Error responses still come back as JSON — same shape as the
    // other helpers. Parse and surface via ApiError so catch blocks
    // can branch on status.
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText;
    const devHandled = recoverFromStaleDevToken(res.status, path);
    const studentHandled = recoverFromStaleStudentToken(res.status, path, detail);
    const multiRoleHandled = recoverFromStaleMultiRoleToken(res.status, path, detail);
    maybeDispatchRevocationEvent(res.status, path, devHandled, studentHandled, multiRoleHandled);
    // ``error_code`` (#977): optional stable disambiguator the backend
    // attaches to some error bodies (e.g. the two distinct 409s on
    // /advance). Most responses omit it → ``null``. Surfaced on
    // ``ApiError.code`` for matchers like ``isStaleVersionConflict``.
    throw new ApiError(res.status, detail, err.error_code ?? null);
  }
  // Mid-body timeout maps to ``ApiError(504)`` — see ``rethrowTimeoutAs504``.
  const blob = await res.blob().catch(rethrowTimeoutAs504);
  const cd = res.headers.get('Content-Disposition') || '';
  // Parse ``filename="x.zip"`` (the shape FastAPI's
  // ``Response(content=..., headers={"Content-Disposition": ...})``
  // produces for the export builders). RFC 5987 ``filename*=`` is
  // not used by the backend yet, so a simple match is sufficient.
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : null;
  return { blob, filename };
}

/**
 * Trigger a browser save dialog for the given blob via the standard
 * create-URL + click-anchor recipe. Works in all modern browsers;
 * jsdom (vitest) skips the actual download but the URL.createObjectURL
 * call is mocked in tests to verify the right blob made it through.
 *
 * The fallback filename is used only when ``filename`` is null — the
 * server-provided name is always preferred.
 */
export function downloadBlob(
  blob: Blob,
  filename: string | null,
  fallbackFilename: string,
): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || fallbackFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the click handler has consumed the URL.
  // Without this, long-lived blob URLs accumulate in memory across
  // repeated exports (the Chrome dev tools tab makes this obvious).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Stream Server-Sent Events from a POST endpoint. Used by the
 * tutoring chat to surface tokens incrementally (issue #90).
 *
 * EventSource (the browser's native SSE consumer) doesn't support
 * POST or custom auth headers, so we use ``fetch`` with body
 * streaming + a manual line parser. Same auth + site-password
 * composition as ``apiPost`` so identical 401 / 5xx handling rules
 * apply.
 *
 * Events MUST be JSON-encoded one-per-``data:`` line. ``onEvent`` is
 * called for each parsed event; the promise resolves when the stream
 * ends cleanly. On HTTP-level failures (4xx/5xx before the stream
 * starts) it throws ``ApiError`` so callers can branch on status,
 * matching the non-streaming helpers.
 *
 * Chunk-timeout (issue #163 item 3): each ``reader.read()`` races a
 * ``chunkTimeoutMs`` (default ``STREAM_CHUNK_TIMEOUT_MS`` = 15s) timer.
 * If NO chunk — token delta OR backend ``:heartbeat`` comment — arrives
 * within the window, the reader is cancelled and a
 * ``StreamConnectionLostError`` is thrown so the caller can surface a
 * "Connection lost — retrying" recovery state instead of hanging on an
 * infinite "Waiting for tutor…" spinner. The backend's ~5s heartbeat
 * keeps a healthy-but-slow stream alive, so the timer only fires when
 * the wire is genuinely dead.
 */
export async function apiPostSSE<T = unknown>(
  path: string,
  body: unknown,
  onEvent: (ev: T) => void,
  opts: { chunkTimeoutMs?: number } = {},
): Promise<void> {
  const chunkTimeoutMs = opts.chunkTimeoutMs ?? STREAM_CHUNK_TIMEOUT_MS;
  // Wall clock for the timeout beacon (#1107) — covers the whole stream
  // attempt so a mid-stream silence reports how far in it stalled.
  const startedAt = Date.now();
  const headers = await composedHeaders(path, {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  });
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText;
    const devHandled = recoverFromStaleDevToken(res.status, path);
    const studentHandled = recoverFromStaleStudentToken(res.status, path, detail);
    const multiRoleHandled = recoverFromStaleMultiRoleToken(res.status, path, detail);
    maybeDispatchRevocationEvent(res.status, path, devHandled, studentHandled, multiRoleHandled);
    // ``error_code`` (#977): optional stable disambiguator the backend
    // attaches to some error bodies (e.g. the two distinct 409s on
    // /advance). Most responses omit it → ``null``. Surfaced on
    // ``ApiError.code`` for matchers like ``isStaleVersionConflict``.
    throw new ApiError(res.status, detail, err.error_code ?? null);
  }
  if (!res.body) {
    throw new ApiError(500, 'Streaming response has no body');
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  // Buffer that accumulates text across reads until we have a
  // complete event delimited by \n\n. SSE chunks can span fetch
  // reads arbitrarily; this buffer absorbs partial events.
  let buffer = '';
  try {
    while (true) {
      // Race the read against a per-chunk timeout. A sentinel object
      // (not a thrown rejection from the timer) lets us tell "timed
      // out" apart from a slow-but-eventual read without a try/catch
      // around the race itself. The timer is cleared on every settled
      // read so it never leaks or fires late after a real chunk landed.
      const value = await readChunkWithTimeout(reader, chunkTimeoutMs);
      if (value === STREAM_DONE) break;
      buffer += decoder.decode(value, { stream: true });
      // Pull out complete events, leave the trailing partial in buffer.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const eventBlock = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const dataLine = eventBlock
          .split('\n')
          .find((l) => l.startsWith('data:'));
        // Lines that aren't ``data:`` events — notably the backend's
        // ``:heartbeat`` SSE comment (#163 item 3) — are skipped here,
        // but they STILL count as a chunk above: arriving bytes settle
        // ``reader.read()`` and reset the chunk-timeout. That's the
        // whole point of the heartbeat — keep a slow stream from
        // tripping the timeout without injecting anything visible.
        if (!dataLine) continue;
        const payload = dataLine.slice('data:'.length).trim();
        if (!payload) continue;
        try {
          onEvent(JSON.parse(payload) as T);
        } catch (e) {
          console.warn('apiPostSSE: bad JSON in event:', payload, e);
        }
      }
    }
  } catch (e) {
    // Stream went silent past the chunk cliff (#1107). HTTP headers had
    // already arrived (we passed ``res.ok`` and acquired the reader), so
    // ``headersReceived: true`` — the discriminator that says "the backend
    // answered, then the wire stalled mid-stream" vs. never connecting.
    // Beacon then re-throw so the caller's "Connection lost — retry"
    // recovery is unaffected.
    if (isStreamConnectionLostError(e)) {
      reportClientTimeout({
        url: `${BASE}${path}`,
        elapsedMs: Date.now() - startedAt,
        kind: 'stream',
        budgetMs: chunkTimeoutMs,
        headersReceived: true,
      });
    }
    throw e;
  } finally {
    // Release the lock on the underlying stream regardless of how we
    // exit (clean end, chunk-timeout, or a thrown parse/abort error) so
    // the connection is torn down rather than left half-open.
    reader.cancel().catch(() => {});
  }
}

/**
 * Sentinel returned by ``readChunkWithTimeout`` when the stream ends
 * cleanly (``reader.read()`` resolved with ``done: true``). Distinct
 * object identity so the caller can ``=== STREAM_DONE`` rather than
 * inspecting a shape.
 */
const STREAM_DONE = Symbol('stream-done');

/**
 * ``reader.read()`` with a per-chunk timeout (issue #163 item 3).
 * Resolves with the chunk bytes, or ``STREAM_DONE`` on a clean end.
 * Rejects with ``StreamConnectionLostError`` if no chunk arrives within
 * ``timeoutMs``.
 */
async function readChunkWithTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
): Promise<Uint8Array | typeof STREAM_DONE> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // The timeout promise rejects; the read promise resolves. Whichever
  // settles first wins the race.
  //
  // Crucially we do NOT cancel the reader from inside the timer: cancel
  // resolves the still-pending ``reader.read()`` with ``{done: true}``,
  // which would then race the rejection and could win — surfacing a
  // spurious CLEAN end instead of the connection-lost error. So the
  // timer ONLY rejects; the reader is cancelled by ``apiPostSSE``'s
  // outer ``finally`` once the rejection has unwound the loop, tearing
  // down the dead connection after the error is already in flight.
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new StreamConnectionLostError());
    }, timeoutMs);
  });
  try {
    const result = await Promise.race([reader.read(), timeoutPromise]);
    const { value, done } = result as ReadableStreamReadResult<Uint8Array>;
    return done ? STREAM_DONE : (value as Uint8Array);
  } finally {
    // Clear on every settle — including the read-wins path — so a
    // pending timer can't fire against a connection that already
    // delivered its chunk.
    if (timer !== undefined) clearTimeout(timer);
  }
}
